import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useCurrency } from "../context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { Plus, KeyRound, Clock, Building2, Star, MessageSquare, Bookmark, Copy, Edit3, Trash2, Rocket, Loader2, Boxes, Download, Upload, Calendar, ArrowUp, ArrowDown, X, RefreshCw, ExternalLink, PartyPopper, Sparkles, Megaphone, RotateCw } from "lucide-react";
import ManagerUpsellModal from "../components/ManagerUpsellModal";
import PushNotificationsToggle from "../components/PushNotificationsToggle";
import HostCommissionCard from "../components/HostCommissionCard";
import HostPayoutMethodCard from "../components/HostPayoutMethodCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HostDashboard() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  // Post-publish celebration state — populated once, on the tick after the
  // wizard navigates in with location.state. We clear the state via
  // history.replaceState so a refresh doesn't re-fire the toast / banner.
  const [celebration, setCelebration] = useState(null);
  const [tab, setTab] = useState("properties");
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [keyDialog, setKeyDialog] = useState(null);
  const [keyValue, setKeyValue] = useState("");
  const [replyDialog, setReplyDialog] = useState(null);
  const [replyText, setReplyText] = useState("");
  // "Save as template" prompt state: which property + its default name.
  const [saveTplFor, setSaveTplFor] = useState(null);
  const [saveTplName, setSaveTplName] = useState("");
  const [saveTplBusy, setSaveTplBusy] = useState(false);
  // "Publish template" prompt state: overrides let hosts adjust just what's
  // different (room number, price) without touching the base template.
  const [publishFor, setPublishFor] = useState(null);
  const [publishOverrides, setPublishOverrides] = useState({});
  const [publishBusy, setPublishBusy] = useState(false);
  const [deletingTpl, setDeletingTpl] = useState(null);
  // Bulk-publish wizard state — creates N listings from one template with
  // auto-numbered titles ("Room {n}" → "Room 201", "Room 202", ...).
  const [bulkFor, setBulkFor] = useState(null);
  const [bulkForm, setBulkForm] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  // Hidden file input ref so the "Import template" button can trigger it.
  const importFileRef = React.useRef(null);
  // Which property is currently mid auto-relist toggle (used to spin the
  // icon and disable double-clicks). Null when idle.
  const [autoRelistBusy, setAutoRelistBusy] = useState(null);
  // "Manage in EasyStay Manager" deep-link is mint-then-redirect. We disable
  // the button during the round-trip so double-clicks don't burn two tokens.
  const [managerOpening, setManagerOpening] = useState(false);
  // Manager subscription state — tiny fetch on load; drives the "EasyStay
  // Manager active" chip in the header. Absent when the host has no sub
  // (which is the common case, so the fetch stays fire-and-forget-cheap).
  const [managerSub, setManagerSub] = useState(null);
  // Advertiser feed for the "My Ads" tab — hosts see every ad they've
  // submitted (approved / pending / expired) so they can Repost the ones
  // that lapsed without re-typing headlines / creatives.
  const [ads, setAds] = useState([]);
  // Repost modal state for LISTINGS — `repostFor` is the property row that
  // opened the modal; `repostForm` holds the datetime-local inputs.
  const [repostFor, setRepostFor] = useState(null);
  const [repostForm, setRepostForm] = useState({ available_from: "", available_until: "" });
  const [repostBusy, setRepostBusy] = useState(false);
  // Repost modal state for ADS — `adRepostFor` is the ad row; `adRepostForm`
  // holds the days chip + optional budget override.
  const [adRepostFor, setAdRepostFor] = useState(null);
  const [adRepostForm, setAdRepostForm] = useState({ days: 30, budget_zmw: "" });
  const [adRepostBusy, setAdRepostBusy] = useState(false);
  // Manager subscription upsell modal — opens automatically after publish
  // for multi-room properties or hosts with more than one listing, and
  // manually via the "Manage professionally" pill on the dashboard.
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [upsellReason, setUpsellReason] = useState(null);

  // Consume any post-publish celebration state passed by NewListing.jsx.
  // Fires a celebratory toast, seeds the banner, then wipes the state from
  // history so a refresh (or a back/forward navigation) doesn't re-fire.
  useEffect(() => {
    const s = location.state;
    if (s?.justPublishedId) {
      setCelebration({
        id: s.justPublishedId,
        title: s.justPublishedTitle || "your listing",
        status: s.justPublishedStatus || "pending",
      });
      toast.success("Listing submitted 🎉", {
        description: s.justPublishedStatus === "pending"
          ? "It'll go live once an admin reviews it — usually within a few hours."
          : "It's live now. Guests can start booking.",
        duration: 6000,
      });
      // Auto-open the Manager upsell modal when the fresh listing has room
      // types (multi-room property) OR when the host now owns more than one
      // property. Matches the spec exactly. The `properties` array might
      // still be loading — we check it in a follow-up effect below.
      if (s.justPublishedHasRoomTypes) {
        setUpsellOpen(true);
        setUpsellReason("Great fit for multi-room properties");
      }
      // Strip state from history so a refresh doesn't re-celebrate.
      window.history.replaceState({}, "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Second-trigger for the upsell — if the host now has 2+ listings, auto-
  // open once per session. Only fires after `properties` loaded so we can
  // count reliably. Skipped if the modal is already opened by the room-type
  // path above.
  useEffect(() => {
    if (upsellOpen) return;
    if (!celebration) return; // only auto-open in the post-publish flow
    if (!Array.isArray(properties)) return;
    if (properties.length >= 2) {
      setUpsellOpen(true);
      setUpsellReason(`You're running ${properties.length} listings`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.length, celebration]);

  // Load the host's Manager subscription status once. Fire-and-forget:
  // if the API fails or the host isn't subscribed we simply don't render
  // the chip, no toast, no retry storm.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/manager/subscription/status`);
        setManagerSub(data);
      } catch (_) {
        // 401 (unauth) or 500 — silently absent chip is fine.
      }
    })();
  }, []);

  const load = () => {
    axios.get(`${API}/my/properties`).then(({ data }) => setProperties(data));
    axios.get(`${API}/host/bookings`).then(({ data }) => setBookings(data));
    axios.get(`${API}/host/review-inbox`).then(({ data }) => setReviews(data)).catch(() => setReviews([]));
    axios.get(`${API}/host/templates`).then(({ data }) => setTemplates(data.items || [])).catch(() => setTemplates([]));
    axios.get(`${API}/host/ads`).then(({ data }) => setAds(data)).catch(() => setAds([]));
  };

  // A listing is "expired" from a host-UX standpoint when either (a) it's
  // been de-activated (is_active === false, e.g. deleted) or (b) its
  // seasonal availability window has closed. Kept as a pure derivation so
  // it re-evaluates on every render without stale state.
  const isPropertyExpired = (p) => {
    if (!p) return false;
    if (p.is_active === false) return true;
    if (p.available_until) {
      const until = new Date(p.available_until);
      if (!isNaN(until.getTime()) && until < new Date()) return true;
    }
    return false;
  };

  // Open the repost modal — seed the datetime-local inputs from the
  // property's last window so hosts can tweak instead of restart.
  const openRepostListing = (p) => {
    const toLocal = (iso) => {
      if (!iso) return "";
      try { return new Date(iso).toISOString().slice(0, 16); } catch { return ""; }
    };
    setRepostFor(p);
    setRepostForm({
      available_from: toLocal(p.available_from),
      available_until: toLocal(p.available_until),
    });
  };
  const submitRepostListing = async () => {
    if (!repostFor) return;
    setRepostBusy(true);
    try {
      const body = {
        available_from: repostForm.available_from ? new Date(repostForm.available_from).toISOString() : null,
        available_until: repostForm.available_until ? new Date(repostForm.available_until).toISOString() : null,
      };
      await axios.post(`${API}/properties/${repostFor.id}/repost`, body);
      toast.success(`"${repostFor.title}" is live again`);
      setRepostFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not repost listing");
    } finally { setRepostBusy(false); }
  };

  const openRepostAd = (a) => {
    setAdRepostFor(a);
    setAdRepostForm({ days: 30, budget_zmw: a.budget_zmw ? String(a.budget_zmw) : "" });
  };
  const submitRepostAd = async () => {
    if (!adRepostFor) return;
    if (!adRepostForm.days || adRepostForm.days < 1) return toast.error("Pick a campaign duration");
    setAdRepostBusy(true);
    try {
      const body = { days: parseInt(adRepostForm.days) };
      if (adRepostForm.budget_zmw) body.budget_zmw = parseFloat(adRepostForm.budget_zmw);
      await axios.post(`${API}/host/ads/${adRepostFor.id}/repost`, body);
      toast.success(`Ad "${adRepostFor.headline}" is running again`);
      setAdRepostFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not repost ad");
    } finally { setAdRepostBusy(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const deliverKey = async () => {
    if (!keyValue || keyValue.length < 4) return toast.error(t("host.keyPlaceholder"));
    try {
      const { data } = await axios.post(`${API}/bookings/deliver-key`, { booking_id: keyDialog.id, digital_key: keyValue });
      const sms = data?.sms || {};
      if (sms.sent) {
        toast.success(`Key delivered · SMS sent to ${keyDialog.guest_phone}`);
      } else if (sms.reason && sms.reason !== "no phone on file") {
        toast.warning(`Key delivered. SMS skipped: ${sms.reason}`);
      } else {
        toast.success("Key delivered to guest");
      }
      setKeyDialog(null); setKeyValue(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not deliver key"); }
  };

  const submitReply = async () => {
    if (!replyText.trim() || replyText.trim().length < 3) return toast.error("Reply must be at least 3 characters");
    try {
      await axios.post(`${API}/reviews/${replyDialog.id}/reply`, { reply: replyText.trim() });
      toast.success("Reply posted");
      setReplyDialog(null); setReplyText(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post reply"); }
  };

  const openSaveTemplate = (p) => {
    setSaveTplFor(p);
    setSaveTplName(p.title);
  };

  // Flip auto-relist for one property via the public API. Optimistic UI —
  // updates the local `properties` array immediately so the badge switches
  // without waiting for the round-trip; reverts on failure. EasyStay Manager
  // hits the same PATCH endpoint under the hood.
  const toggleAutoRelist = async (p) => {
    if (autoRelistBusy) return; // one at a time
    const next = !p.auto_relist;
    setAutoRelistBusy(p.id);
    setProperties((prev) => prev.map((row) => (row.id === p.id ? { ...row, auto_relist: next } : row)));
    try {
      await axios.patch(`${API}/properties/${p.id}/auto-relist`, { enabled: next });
      toast.success(next ? "Auto-relist on — this listing will republish itself after each stay" : "Auto-relist off");
    } catch (e) {
      // Revert optimistic update
      setProperties((prev) => prev.map((row) => (row.id === p.id ? { ...row, auto_relist: !next } : row)));
      toast.error(e.response?.data?.detail || "Could not update auto-relist");
    } finally {
      setAutoRelistBusy(null);
    }
  };

  // Deep-link to EasyStay Manager with a one-time SSO handoff token. The
  // token dies on first read (Manager consumes it via
  // /api/auth/handoff/consume) and expires 60s from now regardless, so a
  // leaked URL is harmless within a minute. Opens in a new tab so the host
  // keeps their EasyStay session open in the original tab.
  const openInManager = async () => {
    if (managerOpening) return;
    setManagerOpening(true);
    // Open the tab synchronously — some browsers block window.open calls
    // that fire after an await. We navigate the placeholder tab once the
    // mint returns.
    const w = window.open("about:blank", "_blank");
    try {
      const { data } = await axios.post(`${API}/auth/handoff/mint`);
      // Send the token in BOTH the fragment (#sso=...) AND the query string
      // (?sso=...) so Manager's SSO consumer works whichever URL surface it
      // reads. Manager's App.jsx snippet (see /app/MANAGER_SSO_SNIPPET.md)
      // checks both. Fragments are safer (never sent to any server) so we
      // prefer them, but some SPA boot flows only look at query strings.
      const token = encodeURIComponent(data.handoff_token);
      const url = `${data.redirect_base}/?sso=${token}#sso=${token}`;
      if (w) {
        w.location.href = url;
      } else {
        // Popup blocked — copy to clipboard as a fallback.
        try { await navigator.clipboard.writeText(url); } catch (_) {}
        toast.warning("Popup blocked. Manager link copied to clipboard — paste it into a new tab.");
      }
    } catch (e) {
      if (w) w.close();
      toast.error(e.response?.data?.detail || "Could not open EasyStay Manager");
    } finally {
      setManagerOpening(false);
    }
  };
  const submitSaveTemplate = async () => {
    if (!saveTplName.trim() || saveTplName.trim().length < 2) return toast.error("Give the template a name");
    try {
      setSaveTplBusy(true);
      await axios.post(`${API}/host/templates/from-property/${saveTplFor.id}`, { name: saveTplName.trim() });
      toast.success(`Saved "${saveTplName.trim()}" — you can relist it in one tap from the Templates tab`);
      setSaveTplFor(null); setSaveTplName("");
      load();
      setTab("templates");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save template"); }
    finally { setSaveTplBusy(false); }
  };

  const duplicateTemplate = async (tpl) => {
    try {
      await axios.post(`${API}/host/templates/${tpl.id}/duplicate`);
      toast.success(`Duplicated "${tpl.name}" — edit and publish the copy separately`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not duplicate template"); }
  };
  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete the template "${tpl.name}"? Existing listings you published from it are NOT affected.`)) return;
    try {
      setDeletingTpl(tpl.id);
      await axios.delete(`${API}/host/templates/${tpl.id}`);
      toast.success(`Template deleted`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete template"); }
    finally { setDeletingTpl(null); }
  };
  const openPublish = (tpl) => {
    setPublishFor(tpl);
    // Copy photos into local state so reorder/delete edits stay per-publish.
    setPublishOverrides({
      title: tpl.title,
      address: tpl.address,
      price_per_night_zmw: tpl.price_per_night_zmw,
      price_per_hour_zmw: tpl.price_per_hour_zmw,
      photos: [...(tpl.photos || [])],
      available_from: "",
      available_until: "",
    });
  };
  const submitPublish = async () => {
    try {
      setPublishBusy(true);
      // Only send fields that differ from the template so the backend falls
      // back to the template value for blank ones.
      const body = {};
      const trim = (v) => (typeof v === "string" ? v.trim() : v);
      if (trim(publishOverrides.title) && trim(publishOverrides.title) !== publishFor.title) body.title = trim(publishOverrides.title);
      if (trim(publishOverrides.address) && trim(publishOverrides.address) !== publishFor.address) body.address = trim(publishOverrides.address);
      if (publishOverrides.price_per_night_zmw && Number(publishOverrides.price_per_night_zmw) !== Number(publishFor.price_per_night_zmw)) body.price_per_night_zmw = Number(publishOverrides.price_per_night_zmw);
      if (publishOverrides.price_per_hour_zmw && Number(publishOverrides.price_per_hour_zmw) !== Number(publishFor.price_per_hour_zmw)) body.price_per_hour_zmw = Number(publishOverrides.price_per_hour_zmw);
      // Photos: only override if the host actually re-ordered / deleted.
      const originalPhotos = publishFor.photos || [];
      const currentPhotos = publishOverrides.photos || [];
      const sameLen = originalPhotos.length === currentPhotos.length;
      const sameOrder = sameLen && originalPhotos.every((p, i) => p === currentPhotos[i]);
      if (!sameOrder) body.photos = currentPhotos;
      // Availability window — send only when set.
      if (publishOverrides.available_from) body.available_from = new Date(publishOverrides.available_from).toISOString();
      if (publishOverrides.available_until) body.available_until = new Date(publishOverrides.available_until).toISOString();
      const { data } = await axios.post(`${API}/host/templates/${publishFor.id}/publish`, body);
      const status = data?.property?.status;
      toast.success(status === "approved"
        ? "Relisted — your new listing is live 🎉"
        : "Relisted — pending admin review");
      setPublishFor(null); setPublishOverrides({});
      load();
      setTab("properties");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not publish template"); }
    finally { setPublishBusy(false); }
  };

  // Move a photo tile up/down in the publish preview.
  const movePhoto = (from, to) => {
    setPublishOverrides((o) => {
      const arr = [...(o.photos || [])];
      if (to < 0 || to >= arr.length) return o;
      const [it] = arr.splice(from, 1);
      arr.splice(to, 0, it);
      return { ...o, photos: arr };
    });
  };
  const removePhoto = (idx) => {
    setPublishOverrides((o) => ({ ...o, photos: (o.photos || []).filter((_, i) => i !== idx) }));
  };

  // Bulk publish handlers.
  const openBulk = (tpl) => {
    setBulkFor(tpl);
    setBulkForm({
      start_number: 201,
      end_number: 210,
      title: `${tpl.title} — Room {n}`,
      address: tpl.address ? `${tpl.address}, Room {n}` : "",
      price_per_night_zmw: tpl.price_per_night_zmw,
      price_per_hour_zmw: tpl.price_per_hour_zmw,
      photos: [...(tpl.photos || [])],
      available_from: "",
      available_until: "",
    });
    setBulkResult(null);
  };
  const submitBulk = async () => {
    try {
      setBulkBusy(true);
      const body = {
        start_number: Number(bulkForm.start_number),
        end_number: Number(bulkForm.end_number),
        title: (bulkForm.title || "").trim(),
      };
      const trim = (v) => (typeof v === "string" ? v.trim() : v);
      if (trim(bulkForm.address)) body.address = trim(bulkForm.address);
      if (bulkForm.price_per_night_zmw && Number(bulkForm.price_per_night_zmw) !== Number(bulkFor.price_per_night_zmw)) body.price_per_night_zmw = Number(bulkForm.price_per_night_zmw);
      if (bulkForm.price_per_hour_zmw && Number(bulkForm.price_per_hour_zmw) !== Number(bulkFor.price_per_hour_zmw)) body.price_per_hour_zmw = Number(bulkForm.price_per_hour_zmw);
      const orig = bulkFor.photos || [];
      const cur = bulkForm.photos || [];
      if (orig.length !== cur.length || !orig.every((p, i) => p === cur[i])) body.photos = cur;
      if (bulkForm.available_from) body.available_from = new Date(bulkForm.available_from).toISOString();
      if (bulkForm.available_until) body.available_until = new Date(bulkForm.available_until).toISOString();
      const { data } = await axios.post(`${API}/host/templates/${bulkFor.id}/publish-bulk`, body);
      setBulkResult(data);
      toast.success(`${data.count} listings created${data.errors?.length ? ` · ${data.errors.length} skipped` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk publish failed"); }
    finally { setBulkBusy(false); }
  };

  // Export template as JSON file the host can share.
  const exportTemplate = async (tpl) => {
    try {
      const { data } = await axios.get(`${API}/host/templates/${tpl.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (tpl.name || "template").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      a.href = url;
      a.download = `easystay-template-${safeName}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported "${tpl.name}" as JSON`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not export template"); }
  };

  const importTemplate = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("File too large (max 500 KB)");
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const { data } = await axios.post(`${API}/host/templates/import`, json);
      toast.success(`Imported "${data.name}"`);
      load();
      setTab("templates");
    } catch (e) {
      if (e instanceof SyntaxError) {
        toast.error("That file isn't valid JSON");
      } else {
        toast.error(e.response?.data?.detail || "Could not import template");
      }
    }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <h1 className="font-display text-3xl font-bold">{t("host.signInPrompt")}</h1>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("host.title")}</h1>
          <p className="mt-1 text-slate-600">{t("host.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {managerSub?.active && (
            <div
              data-testid="manager-active-chip"
              title={`EasyStay Manager — ${managerSub.plan} plan · active`}
              className="inline-flex items-center gap-1.5 rounded-full border border-forest/30 bg-forest/10 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-forest"
            >
              <Sparkles className="h-3 w-3" />
              Manager Active
              <span className="text-[10px] font-semibold normal-case tracking-normal opacity-80">· {managerSub.plan}</span>
            </div>
          )}
          {!managerSub?.active && (
            <button
              onClick={() => {
                setUpsellReason("Manager subscription");
                setUpsellOpen(true);
              }}
              data-testid="open-upsell-btn"
              className="hidden items-center gap-1.5 rounded-full border border-forest/40 bg-forest/5 px-3 py-2 text-xs font-semibold text-forest transition hover:bg-forest/10 sm:inline-flex"
              title="Manage your property professionally with EasyStay Manager"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Manage professionally
            </button>
          )}
          <button
            onClick={openInManager}
            data-testid="open-in-manager-btn"
            disabled={managerOpening}
            className="group relative flex items-center gap-2 overflow-hidden rounded-full border border-copper/40 bg-gradient-to-r from-copper/10 to-forest/10 px-4 py-2.5 text-sm font-semibold text-copper transition hover:from-copper/20 hover:to-forest/20 disabled:opacity-60"
            title="Open EasyStay Manager in a new tab — you'll be signed in automatically"
          >
            {managerOpening ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            Manage in EasyStay Manager
          </button>
          <button onClick={() => nav("/host/new")} data-testid="new-listing-btn" className="btn-primary text-sm">
            <Plus className="mr-1 inline h-4 w-4" /> {t("host.newListing")}
          </button>
        </div>
      </div>

      {/* Mount the upsell modal once at the top level — controlled state
          decides visibility, so we don't need Portals or context. */}
      <ManagerUpsellModal
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        reason={upsellReason}
      />

      <div className="mt-6 space-y-3">
        {celebration && (
          <div
            data-testid="listing-published-banner"
            className="relative flex flex-wrap items-center gap-4 overflow-hidden rounded-3xl border border-copper/30 bg-gradient-to-br from-copper/10 via-white to-forest/10 p-5 card-shadow"
          >
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-copper/10" aria-hidden />
            <div className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-forest/10" aria-hidden />
            <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-copper text-white shadow-lg shadow-copper/25">
              <PartyPopper className="h-6 w-6" />
            </div>
            <div className="relative min-w-0 flex-1">
              <div className="font-display text-lg font-bold text-slate-900">
                Listing submitted 🎉
              </div>
              <div className="mt-0.5 text-sm text-slate-600">
                <b>“{celebration.title}”</b> is {celebration.status === "pending"
                  ? "pending admin review — you'll get a notification the moment it goes live."
                  : "live and searchable now. Guests can start booking."}
              </div>
            </div>
            <div className="relative flex flex-wrap items-center gap-2">
              <Link
                to={`/property/${celebration.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-my-listing-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-copper px-4 py-2 text-sm font-semibold text-white shadow-md shadow-copper/25 transition hover:bg-copper/90"
              >
                View my listing <ExternalLink className="h-3.5 w-3.5" />
              </Link>
              <button
                onClick={() => setCelebration(null)}
                data-testid="dismiss-celebration-btn"
                aria-label="Dismiss"
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-white hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        <HostCommissionCard />
        <HostPayoutMethodCard />
        <PushNotificationsToggle />
      </div>

      <div className="mt-8 flex gap-2 border-b border-slate-200 overflow-x-auto">
        {[
          { id: "properties", label: t("host.tabs.properties"), count: properties.length },
          { id: "bookings", label: t("host.tabs.bookings"), count: bookings.length },
          { id: "reviews", label: t("host.tabs.reviews"), count: reviews.length },
          { id: "templates", label: "Templates", count: templates.length },
          { id: "ads", label: "My ads", count: ads.length },
        ].map((tabItem) => (
          <button key={tabItem.id} onClick={() => setTab(tabItem.id)} data-testid={`tab-${tabItem.id}`}
            className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-semibold transition ${tab === tabItem.id ? "border-copper text-copper" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {tabItem.label} · {tabItem.count}
          </button>
        ))}
      </div>

      {tab === "properties" && (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {properties.length === 0 ? (
            <div className="col-span-full rounded-3xl bg-white p-10 text-center">
              <Building2 className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-slate-500">{t("host.propsEmpty")}</p>
              <button onClick={() => nav("/host/new")} className="btn-primary mt-4 text-sm">{t("host.addFirst")}</button>
            </div>
          ) : properties.map((p) => {
            const expired = isPropertyExpired(p);
            return (
            <div key={p.id} data-testid={`host-property-${p.id}`} className="rounded-3xl bg-white p-5 card-shadow">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-display text-lg font-bold">{p.title}</div>
                    {expired && (
                      <span
                        data-testid={`expired-badge-${p.id}`}
                        className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800"
                      >
                        Expired
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500">{p.city} · {p.type.replace("_", " ")}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold">{format(p.price_per_night_zmw)}</div>
                  <div className="text-xs text-slate-500">{t("host.perNight")}</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2 text-xs">
                <span className="chip">{t("host.minLabel", { h: p.min_hours })}</span>
                <span className="chip">{t("host.guests", { n: p.max_guests })}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {expired && (
                  <button
                    onClick={() => openRepostListing(p)}
                    data-testid={`repost-listing-${p.id}`}
                    className="flex items-center gap-1 rounded-full bg-copper px-3 py-2 text-xs font-bold text-white shadow-md shadow-copper/25 transition hover:bg-copper/90"
                  >
                    <RotateCw className="h-3.5 w-3.5" /> Repost
                  </button>
                )}
                <Link to={`/property/${p.id}`} className="btn-outline text-xs !py-2">{t("host.view")}</Link>
                <button
                  onClick={() => openSaveTemplate(p)}
                  data-testid={`save-template-${p.id}`}
                  className="flex items-center gap-1 rounded-full border border-copper/40 bg-white px-3 py-2 text-xs font-semibold text-copper hover:bg-copper/5"
                >
                  <Bookmark className="h-3.5 w-3.5" /> Save as template
                </button>
                <button
                  onClick={() => toggleAutoRelist(p)}
                  data-testid={`auto-relist-toggle-${p.id}`}
                  disabled={autoRelistBusy === p.id}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition ${
                    p.auto_relist
                      ? "border-forest/40 bg-forest/10 text-forest hover:bg-forest/15"
                      : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={p.auto_relist ? "Auto-relist ON — this listing republishes automatically when its booking ends or is cancelled" : "Turn on auto-relist so EasyStay republishes this listing automatically"}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${autoRelistBusy === p.id ? "animate-spin" : ""}`} />
                  Auto-relist {p.auto_relist ? "ON" : "OFF"}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {tab === "bookings" && (
        <div className="mt-6 space-y-3">
          {bookings.length === 0 ? (
            <div className="rounded-3xl bg-white p-10 text-center text-slate-500">{t("host.noBookings")}</div>
          ) : bookings.map((b) => (
            <div key={b.id} data-testid={`host-booking-${b.id}`} className="rounded-3xl bg-white p-5 card-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-lg font-bold">{b.property_title}</div>
                  <div className="mt-1 text-sm text-slate-600">
                    {t("host.guest")} <span className="font-medium">{b.guest_name}</span> · {b.guest_email}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {new Date(b.check_in).toLocaleString()} · {b.duration_hours}h</span>
                    <span className="flex items-center gap-1 capitalize"><KeyRound className="h-4 w-4" /> {b.key_type}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold">{format(b.total_zmw)}</div>
                  <div className={`mt-1 inline-block rounded-full px-2 py-1 text-xs font-semibold ${b.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"}`}>
                    {b.payment_status}
                  </div>
                </div>
              </div>
              {b.payment_status === "paid" && b.key_type === "digital" && !b.key_delivered && (
                <button data-testid={`deliver-key-${b.id}`} onClick={() => setKeyDialog(b)} className="btn-primary mt-3 text-sm">
                  <KeyRound className="mr-1 inline h-4 w-4" /> {t("host.sendDigitalKey")}
                </button>
              )}
              {b.key_delivered && (
                <div className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{t("host.keyDelivered")} <span className="font-mono font-bold">{b.digital_key}</span></div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "reviews" && (
        <div className="mt-6 space-y-3" data-testid="host-review-inbox">
          {reviews.length === 0 ? (
            <div className="rounded-3xl bg-white p-10 text-center text-slate-500">
              <MessageSquare className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3">{t("host.noReviews")}</p>
            </div>
          ) : reviews.map((r) => (
            <div key={r.id} data-testid={`host-review-${r.id}`} className="rounded-3xl bg-white p-5 card-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-slate-500">{r.property_title}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-copper text-copper" : "text-slate-200"}`} />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-slate-900">{r.guest_name}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-700">{r.comment}</p>
                </div>
                <div className="text-right text-xs text-slate-500">
                  {new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                </div>
              </div>
              {r.owner_reply ? (
                <div className="mt-3 rounded-2xl border-l-2 border-copper bg-copper/5 p-3">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-copper">
                    <MessageSquare className="h-3.5 w-3.5" /> {t("host.yourReply")}
                  </div>
                  <p className="mt-1 text-sm text-slate-700">{r.owner_reply}</p>
                </div>
              ) : (
                <button
                  onClick={() => { setReplyDialog(r); setReplyText(""); }}
                  data-testid={`open-reply-${r.id}`}
                  className="btn-outline mt-3 text-xs !py-2"
                >
                  <MessageSquare className="mr-1 inline h-3.5 w-3.5" /> {t("host.replyPublicly")}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "templates" && (
        <div className="mt-6 space-y-3" data-testid="templates-list">
          <div className="flex justify-end">
            <input
              ref={importFileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              data-testid="import-template-file"
              onChange={(e) => { const f = e.target.files?.[0]; importTemplate(f); e.target.value = ""; }}
            />
            <button
              type="button"
              onClick={() => importFileRef.current?.click()}
              data-testid="import-template-btn"
              className="flex items-center gap-1.5 rounded-full border border-forest/40 bg-white px-3 py-1.5 text-xs font-semibold text-forest hover:bg-forest/5"
            >
              <Upload className="h-3.5 w-3.5" /> Import template
            </button>
          </div>
          {templates.length === 0 ? (
            <div className="rounded-3xl bg-white p-10 text-center text-slate-500" data-testid="templates-empty">
              <Bookmark className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3">No saved templates yet.</p>
              <p className="mt-1 text-xs text-slate-400">Save any existing listing as a template from the Properties tab, or import a JSON template shared by a colleague.</p>
            </div>
          ) : templates.map((tpl) => (
            <div key={tpl.id} data-testid={`template-${tpl.id}`} className="rounded-3xl bg-white p-5 card-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-display text-lg font-bold text-slate-900" data-testid={`template-name-${tpl.id}`}>{tpl.name}</div>
                    {tpl.publish_count > 0 && (
                      <span className="rounded-full bg-forest/10 px-2 py-0.5 text-[10px] font-semibold text-forest">
                        Published {tpl.publish_count}×
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">{tpl.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{tpl.city} · {String(tpl.type || "").replace("_", " ")} · min {tpl.min_hours}h · {tpl.max_guests} guests · {tpl.photos?.length || 0} photos</div>
                  {tpl.last_published_at && (
                    <div className="mt-1 text-[11px] text-slate-400">Last relisted {new Date(tpl.last_published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold text-slate-900">{format(tpl.price_per_night_zmw)}</div>
                  <div className="text-xs text-slate-500">{t("host.perNight")}</div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => openPublish(tpl)}
                  data-testid={`publish-template-${tpl.id}`}
                  className="btn-primary flex items-center gap-1 text-xs !py-2"
                >
                  <Rocket className="h-3.5 w-3.5" /> Relist now
                </button>
                <button
                  onClick={() => openBulk(tpl)}
                  data-testid={`bulk-publish-${tpl.id}`}
                  className="flex items-center gap-1 rounded-full border border-forest/40 bg-white px-3 py-2 text-xs font-semibold text-forest hover:bg-forest/5"
                >
                  <Boxes className="h-3.5 w-3.5" /> Bulk relist
                </button>
                <button
                  onClick={() => duplicateTemplate(tpl)}
                  data-testid={`duplicate-template-${tpl.id}`}
                  className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-copper hover:text-copper"
                >
                  <Copy className="h-3.5 w-3.5" /> Duplicate
                </button>
                <button
                  onClick={() => { setPublishFor(null); setSaveTplFor(null); setPublishOverrides({}); setSaveTplName(tpl.name); setSaveTplFor({ __edit: true, ...tpl }); }}
                  data-testid={`edit-template-${tpl.id}`}
                  className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-copper hover:text-copper"
                >
                  <Edit3 className="h-3.5 w-3.5" /> Edit name
                </button>
                <button
                  onClick={() => exportTemplate(tpl)}
                  data-testid={`export-template-${tpl.id}`}
                  className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-forest hover:text-forest"
                >
                  <Download className="h-3.5 w-3.5" /> Export
                </button>
                <button
                  onClick={() => deleteTemplate(tpl)}
                  data-testid={`delete-template-${tpl.id}`}
                  disabled={deletingTpl === tpl.id}
                  className="ml-auto flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 hover:border-rose-400 disabled:opacity-50"
                >
                  {deletingTpl === tpl.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "ads" && (
        <div className="mt-6 space-y-3" data-testid="host-ads-tab">
          {ads.length === 0 ? (
            <div className="rounded-3xl bg-white p-10 text-center text-slate-500">
              <Megaphone className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3">You haven't submitted any ads yet.</p>
              <Link to="/advertise" className="btn-primary mt-4 inline-block text-sm">
                Submit an ad
              </Link>
            </div>
          ) : ads.map((a) => (
            <div key={a.id} data-testid={`host-ad-${a.id}`} className="rounded-3xl bg-white p-5 card-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-display text-lg font-bold text-slate-900">{a.headline}</div>
                    {a.is_expired && (
                      <span
                        data-testid={`ad-expired-badge-${a.id}`}
                        className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800"
                      >
                        Expired
                      </span>
                    )}
                    {a.status === "pending" && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-700">
                        Pending review
                      </span>
                    )}
                    {a.status === "rejected" && (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rose-700">
                        Rejected
                      </span>
                    )}
                    {a.status === "approved" && !a.is_expired && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                        Live
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    <span className="font-semibold">{a.advertiser}</span>
                    {a.body ? ` — ${a.body}` : ""}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                    <span>{a.impressions || 0} impressions</span>
                    <span>·</span>
                    <span>{a.clicks || 0} clicks</span>
                    {a.budget_zmw != null && (<>
                      <span>·</span>
                      <span>Budget {format(a.budget_zmw)}</span>
                    </>)}
                    {a.ends_at && (<>
                      <span>·</span>
                      <span>Ends {new Date(a.ends_at).toLocaleDateString()}</span>
                    </>)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {a.is_expired && (
                    <button
                      onClick={() => openRepostAd(a)}
                      data-testid={`repost-ad-${a.id}`}
                      className="flex items-center gap-1 rounded-full bg-copper px-3 py-2 text-xs font-bold text-white shadow-md shadow-copper/25 transition hover:bg-copper/90"
                    >
                      <RotateCw className="h-3.5 w-3.5" /> Repost
                    </button>
                  )}
                  {a.link_url && (
                    <a
                      href={a.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-copper hover:text-copper"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Preview
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Repost LISTING modal — hosts confirm or update the availability
          window before we flip is_active back on. Every other field on the
          property is preserved as-is by the backend. */}
      <Dialog open={!!repostFor} onOpenChange={(o) => !o && !repostBusy && setRepostFor(null)}>
        <DialogContent className="max-w-md" data-testid="repost-listing-dialog">
          <DialogHeader>
            <DialogTitle>Repost "{repostFor?.title}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              All photos, description, pricing, and room types stay exactly as they were — just confirm or update when this listing should be live.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Live from</label>
                <input
                  type="datetime-local"
                  data-testid="repost-listing-from"
                  value={repostForm.available_from}
                  onChange={(e) => setRepostForm((f) => ({ ...f, available_from: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-copper focus:bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Live until</label>
                <input
                  type="datetime-local"
                  data-testid="repost-listing-until"
                  value={repostForm.available_until}
                  onChange={(e) => setRepostForm((f) => ({ ...f, available_until: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-copper focus:bg-white"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Leave both blank to keep the listing live indefinitely.</p>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setRepostFor(null)} disabled={repostBusy} className="btn-outline text-sm">Cancel</button>
            <button
              type="button"
              onClick={submitRepostListing}
              disabled={repostBusy}
              data-testid="repost-listing-submit"
              className="btn-primary text-sm disabled:opacity-50"
            >
              {repostBusy ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : <RotateCw className="mr-1 inline h-3.5 w-3.5" />}
              Repost listing
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repost AD modal — advertisers confirm or update campaign duration
          + budget before we flip is_active back on. Creative fields
          (headline, body, image, video, link, accent color) are preserved
          as-is by the backend. */}
      <Dialog open={!!adRepostFor} onOpenChange={(o) => !o && !adRepostBusy && setAdRepostFor(null)}>
        <DialogContent className="max-w-md" data-testid="repost-ad-dialog">
          <DialogHeader>
            <DialogTitle>Repost "{adRepostFor?.headline}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Your creative stays exactly the same — headline, image, link, accent color. Just pick how long the campaign runs and confirm the budget.
            </p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Campaign duration</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="repost-ad-days-chips">
                {[7, 14, 30, 60, 90].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setAdRepostForm((f) => ({ ...f, days: d }))}
                    data-testid={`repost-ad-days-${d}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      adRepostForm.days === d
                        ? "border-copper bg-copper text-white shadow-md shadow-copper/20"
                        : "border-slate-200 bg-white text-slate-700 hover:border-copper hover:text-copper"
                    }`}
                  >
                    {d} days
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={1}
                max={365}
                data-testid="repost-ad-days-input"
                value={adRepostForm.days}
                onChange={(e) => setAdRepostForm((f) => ({ ...f, days: parseInt(e.target.value) || 30 }))}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-copper focus:bg-white"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Budget (ZMW)</label>
              <input
                type="number"
                min={0}
                step="0.01"
                data-testid="repost-ad-budget"
                value={adRepostForm.budget_zmw}
                onChange={(e) => setAdRepostForm((f) => ({ ...f, budget_zmw: e.target.value }))}
                placeholder={adRepostFor?.budget_zmw != null ? `${adRepostFor.budget_zmw}` : "e.g. 500"}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
              />
              <p className="mt-1 text-[11px] text-slate-400">Leave blank to keep the previous budget.</p>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setAdRepostFor(null)} disabled={adRepostBusy} className="btn-outline text-sm">Cancel</button>
            <button
              type="button"
              onClick={submitRepostAd}
              disabled={adRepostBusy}
              data-testid="repost-ad-submit"
              className="btn-primary text-sm disabled:opacity-50"
            >
              {adRepostBusy ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : <RotateCw className="mr-1 inline h-3.5 w-3.5" />}
              Repost ad
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save-as-template + rename-template dialog (dual-purpose). */}
      <Dialog open={!!saveTplFor} onOpenChange={(o) => !o && !saveTplBusy && setSaveTplFor(null)}>
        <DialogContent className="max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle>{saveTplFor?.__edit ? "Rename this template" : "Save this listing as a template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Template name</label>
            <input
              data-testid="save-template-name"
              value={saveTplName}
              onChange={(e) => setSaveTplName(e.target.value)}
              placeholder="e.g. Deluxe river-view suite"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
            />
            <p className="text-xs text-slate-500">
              {saveTplFor?.__edit
                ? "You can rename the template without affecting listings you've already published from it."
                : "Everything from this listing (photos, amenities, pricing, key handoff) is copied. You can duplicate + tweak it later."}
            </p>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setSaveTplFor(null)} disabled={saveTplBusy} className="btn-outline text-sm">Cancel</button>
            <button
              type="button"
              disabled={saveTplBusy}
              data-testid="save-template-submit"
              onClick={async () => {
                if (saveTplFor?.__edit) {
                  // Rename path — PATCH keeping every existing field.
                  if (!saveTplName.trim() || saveTplName.trim().length < 2) return toast.error("Give the template a name");
                  try {
                    setSaveTplBusy(true);
                    const body = { ...saveTplFor, name: saveTplName.trim() };
                    delete body.__edit; delete body.id; delete body.created_at; delete body.updated_at;
                    delete body.publish_count; delete body.last_published_at; delete body.source_property_id;
                    await axios.patch(`${API}/host/templates/${saveTplFor.id}`, body);
                    toast.success("Template renamed");
                    setSaveTplFor(null); setSaveTplName("");
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Could not rename template"); }
                  finally { setSaveTplBusy(false); }
                } else {
                  await submitSaveTemplate();
                }
              }}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {saveTplBusy ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : <Bookmark className="mr-1 inline h-3.5 w-3.5" />}
              {saveTplFor?.__edit ? "Save name" : "Save template"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish template dialog — allow adjusting title / address / prices / photos / availability before relisting. */}
      <Dialog open={!!publishFor} onOpenChange={(o) => !o && !publishBusy && setPublishFor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="publish-template-dialog">
          <DialogHeader>
            <DialogTitle>Relist "{publishFor?.name}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Adjust only what's different (e.g. room number, availability). Everything else stays from the template.</p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Listing title</label>
              <input
                data-testid="publish-title-input"
                value={publishOverrides.title || ""}
                onChange={(e) => setPublishOverrides((o) => ({ ...o, title: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Address</label>
              <input
                data-testid="publish-address-input"
                value={publishOverrides.address || ""}
                onChange={(e) => setPublishOverrides((o) => ({ ...o, address: e.target.value }))}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Hourly (K)</label>
                <input type="number" data-testid="publish-hourly-input"
                  value={publishOverrides.price_per_hour_zmw || ""}
                  onChange={(e) => setPublishOverrides((o) => ({ ...o, price_per_hour_zmw: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nightly (K)</label>
                <input type="number" data-testid="publish-nightly-input"
                  value={publishOverrides.price_per_night_zmw || ""}
                  onChange={(e) => setPublishOverrides((o) => ({ ...o, price_per_night_zmw: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Availability window (optional)
              </label>
              <p className="text-[11px] text-slate-400 mt-1">Blank = live immediately. Set a future start to schedule seasonal openings.</p>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <input type="datetime-local" data-testid="publish-available-from-input"
                  value={publishOverrides.available_from || ""}
                  onChange={(e) => setPublishOverrides((o) => ({ ...o, available_from: e.target.value }))}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-copper focus:bg-white" />
                <input type="datetime-local" data-testid="publish-available-until-input"
                  value={publishOverrides.available_until || ""}
                  onChange={(e) => setPublishOverrides((o) => ({ ...o, available_until: e.target.value }))}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-copper focus:bg-white" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Photos ({publishOverrides.photos?.length || 0})</label>
              <p className="text-[11px] text-slate-400 mt-1">Reorder with the arrows, or delete photos that don't apply to this room. Changes only affect this publish.</p>
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="publish-photo-grid">
                {(publishOverrides.photos || []).length === 0 ? (
                  <div className="col-span-3 rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-[11px] text-slate-400">
                    No photos will be used for this listing.
                  </div>
                ) : (publishOverrides.photos || []).map((p, i) => (
                  <div key={`${p}-${i}`} data-testid={`publish-photo-tile-${i}`} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                    <img src={p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`} alt="" className="h-24 w-full object-cover" onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=300"; }} />
                    <div className="absolute inset-x-0 top-0 flex justify-between p-1 opacity-0 transition group-hover:opacity-100">
                      <button type="button" data-testid={`publish-photo-up-${i}`} onClick={() => movePhoto(i, i - 1)}
                        className="rounded-full bg-white/90 p-1 text-slate-700 hover:bg-white" disabled={i === 0}>
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button type="button" data-testid={`publish-photo-down-${i}`} onClick={() => movePhoto(i, i + 1)}
                        className="rounded-full bg-white/90 p-1 text-slate-700 hover:bg-white" disabled={i === (publishOverrides.photos || []).length - 1}>
                        <ArrowDown className="h-3 w-3" />
                      </button>
                      <button type="button" data-testid={`publish-photo-remove-${i}`} onClick={() => removePhoto(i)}
                        className="rounded-full bg-white/90 p-1 text-rose-600 hover:bg-rose-50">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="absolute bottom-0 left-0 rounded-tr-xl bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">#{i + 1}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setPublishFor(null)} disabled={publishBusy} className="btn-outline text-sm">Cancel</button>
            <button type="button" onClick={submitPublish} disabled={publishBusy} data-testid="publish-template-submit" className="btn-primary text-sm disabled:opacity-50">
              {publishBusy ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : <Rocket className="mr-1 inline h-3.5 w-3.5" />} Relist now
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk publish wizard — auto-numbered rooms. */}
      <Dialog open={!!bulkFor} onOpenChange={(o) => !o && !bulkBusy && setBulkFor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-publish-dialog">
          <DialogHeader>
            <DialogTitle>Bulk relist "{bulkFor?.name}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Publish an entire range of rooms in one submit. Use <code className="rounded bg-slate-100 px-1 font-mono">{"{n}"}</code> as the room-number placeholder inside the title and address — we'll fill it in for each listing.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">From room</label>
                <input type="number" data-testid="bulk-start-input"
                  value={bulkForm.start_number ?? ""}
                  onChange={(e) => setBulkForm((f) => ({ ...f, start_number: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-forest focus:bg-white" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">To room</label>
                <input type="number" data-testid="bulk-end-input"
                  value={bulkForm.end_number ?? ""}
                  onChange={(e) => setBulkForm((f) => ({ ...f, end_number: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-forest focus:bg-white" />
              </div>
            </div>
            <div className="rounded-xl bg-forest/5 px-3 py-2 text-[11px] text-forest">
              Will create <b>{Math.max(0, Number(bulkForm.end_number || 0) - Number(bulkForm.start_number || 0) + 1)}</b> listings (max 50 per submit).
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Title pattern</label>
              <input data-testid="bulk-title-input"
                value={bulkForm.title || ""}
                onChange={(e) => setBulkForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Deluxe Suite — Room {n}"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-forest focus:bg-white" />
              <p className="text-[11px] text-slate-400 mt-1">
                Preview: <span className="font-mono text-slate-700">{(bulkForm.title || "").replace("{n}", String(bulkForm.start_number || 1))}</span>
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Address pattern (optional)</label>
              <input data-testid="bulk-address-input"
                value={bulkForm.address || ""}
                onChange={(e) => setBulkForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="12 Sichango Rd, Room {n}"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-forest focus:bg-white" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Hourly (K)</label>
                <input type="number" data-testid="bulk-hourly-input"
                  value={bulkForm.price_per_hour_zmw ?? ""}
                  onChange={(e) => setBulkForm((f) => ({ ...f, price_per_hour_zmw: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-forest focus:bg-white" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nightly (K)</label>
                <input type="number" data-testid="bulk-nightly-input"
                  value={bulkForm.price_per_night_zmw ?? ""}
                  onChange={(e) => setBulkForm((f) => ({ ...f, price_per_night_zmw: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-forest focus:bg-white" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Availability window (applies to every room)
              </label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <input type="datetime-local" data-testid="bulk-available-from-input"
                  value={bulkForm.available_from || ""}
                  onChange={(e) => setBulkForm((f) => ({ ...f, available_from: e.target.value }))}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-forest focus:bg-white" />
                <input type="datetime-local" data-testid="bulk-available-until-input"
                  value={bulkForm.available_until || ""}
                  onChange={(e) => setBulkForm((f) => ({ ...f, available_until: e.target.value }))}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs outline-none focus:border-forest focus:bg-white" />
              </div>
            </div>
            {bulkResult && (
              <div data-testid="bulk-result-summary" className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-700">
                <b className="text-forest">{bulkResult.count}</b> listings created
                {bulkResult.errors?.length ? <> · <b className="text-rose-600">{bulkResult.errors.length}</b> skipped</> : null}
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setBulkFor(null)} disabled={bulkBusy} className="btn-outline text-sm">Close</button>
            <button type="button" onClick={submitBulk} disabled={bulkBusy} data-testid="bulk-publish-submit" className="btn-primary text-sm disabled:opacity-50">
              {bulkBusy ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : <Boxes className="mr-1 inline h-3.5 w-3.5" />} Publish batch
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replyDialog} onOpenChange={(o) => !o && setReplyDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("host.replyTo", { name: replyDialog?.guest_name })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">&ldquo;{replyDialog?.comment}&rdquo;</div>
            <textarea
              data-testid="review-reply-input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value.slice(0, 800))}
              rows={4}
              placeholder={t("host.replyPlaceholder")}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
            />
            <div className="text-right text-xs text-slate-400">{replyText.length}/800</div>
          </div>
          <DialogFooter>
            <button onClick={() => setReplyDialog(null)} className="btn-outline text-sm">{t("host.cancel")}</button>
            <button onClick={submitReply} data-testid="submit-reply-btn" className="btn-primary text-sm">{t("host.postReply")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!keyDialog} onOpenChange={(o) => !o && setKeyDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("host.sendKeyDialogTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{t("host.keyDialogHelp", {
              name: keyDialog?.guest_name,
              smsSuffix: keyDialog?.guest_phone ? t("host.keyDialogSmsSuffix", { phone: keyDialog.guest_phone }) : "",
            })}</p>
            <input
              data-testid="key-input"
              value={keyValue} onChange={(e) => setKeyValue(e.target.value.toUpperCase())}
              placeholder={t("host.keyPlaceholder")}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono outline-none focus:border-copper focus:bg-white"
            />
            {keyDialog?.key_sms_status && (
              <div className={`text-xs ${keyDialog.key_sms_status.sent ? "text-forest" : "text-slate-500"}`}>
                {t("host.lastSms", { status: keyDialog.key_sms_status.sent ? t("host.lastSmsSent") : (keyDialog.key_sms_status.reason || t("host.lastSmsFailed")) })}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setKeyDialog(null)} className="btn-outline text-sm">{t("host.cancel")}</button>
            <button onClick={deliverKey} data-testid="confirm-deliver-key-btn" className="btn-primary text-sm">{t("host.deliverKey")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
